<template>
    <DsModalDialog v-model="isModalVisible" @update:model-value="closeModal">
        <template #top>
            <DsTopSlotHeadlineSubheadline headline="Vyplňte své kontaktní údaje" />
        </template>
        <template #content>
            <form class="contest-form" @submit.prevent="submitForm">
                <div class="contest-form__group bs-row">
                    <div class="ds-input-old ds-input-old--text c-12 c-sm-6">
                        <label class="ds-input-old__label" for="form-first-name"
                            >Jméno<span class="contest-form__required-marker"> *</span></label
                        >
                        <div class="ds-input-old__wrapper">
                            <input
                                required
                                type="text"
                                class="ds-input-old__input"
                                v-model.trim="formData.firstName"
                                name="form-first-name"
                                id="form-first-name"
                                autocomplete="given-name"
                                maxlength="100"
                            />
                        </div>
                    </div>
                    <div class="ds-input-old ds-input-old--text c-12 c-sm-6">
                        <label class="ds-input-old__label" for="form-last-name"
                            >Příjmení<span class="contest-form__required-marker"> *</span></label
                        >
                        <div class="ds-input-old__wrapper">
                            <input
                                required
                                type="text"
                                class="ds-input-old__input"
                                v-model.trim="formData.lastName"
                                name="form-last-name"
                                id="form-last-name"
                                autocomplete="family-name"
                                maxlength="100"
                            />
                        </div>
                    </div>
                    <div class="ds-input-old ds-input-old--tel c-12 c-sm-6">
                        <label class="ds-input-old__label" for="form-phone"
                            >Telefonní číslo
                            <span v-if="configuration.isPhoneRequired" class="contest-form__required-marker"> *</span>
                        </label>
                        <div class="ds-input-old__wrapper">
                            <input
                                :required="configuration.isPhoneRequired"
                                type="tel"
                                class="ds-input-old__input--icon ds-input-old__input telephone"
                                v-model.trim="formData.phone"
                                name="form-phone"
                                id="form-phone"
                                v-maska="'#########'"
                                maxlength="9"
                            />
                            <div class="ds-input-old__icon ds-input-old__icon--flag-cz-rectangle">
                                <DsIcon name="flag-cz-rectangle" :width="24" :height="24" />
                            </div>
                        </div>
                    </div>
                    <div class="ds-input-old ds-input-old--text c-12 c-sm-6">
                        <label class="ds-input-old__label" for="form-email"
                            >E-mail
                            <span v-if="configuration.isEmailRequired" class="contest-form__required-marker">
                                *</span
                            ></label
                        >
                        <div class="ds-input-old__wrapper">
                            <input
                                :required="configuration.isEmailRequired"
                                type="email"
                                class="ds-input-old__input"
                                v-model.trim="formData.email"
                                name="form-email"
                                id="form-email"
                                maxlength="320"
                            />
                        </div>
                    </div>
                </div>
                <div class="contest-form__group bs-row">
                    <div class="ds-input-old ds-input-old--checkbox c-12 c-sm2-6">
                        <label class="ds-checkbox-old">
                            <input
                                required
                                type="checkbox"
                                class="ds-checkbox-old__inp"
                                name="form-agreement"
                                id="form-agreement"
                                v-model="formData.hasAgreed"
                            />
                            <DsIcon name="check" class="ds-checkbox-old__check" />
                            <span class="ds-checkbox-old__inner-text-wrap contest-form__required-wrapper">
                                <span
                                    @click="handleRulesClick"
                                    class="ds-checkbox-old__text"
                                    v-html="configuration.agreementText"
                                ></span>
                                <span class="contest-form__required-marker"> *</span>
                            </span>
                        </label>
                    </div>
                </div>
                <div class="contest-form__group bs-row">
                    <div class="ds-input-old ds-input-old--textarea c-12">
                        <label class="ds-input-old__label contest-form__required-wrapper" for="form-text"
                            ><span v-html="configuration.questionText"></span
                            ><span class="contest-form__required-marker"> *</span></label
                        >
                        <div class="ds-input-old__wrapper">
                            <textarea
                                required
                                type="text"
                                class="ds-input-old__input"
                                v-model.trim="formData.text"
                                name="form-text"
                                id="form-text"
                            ></textarea>
                        </div>
                    </div>
                </div>
                <div class="contest-form__error" v-if="formErrorMessage">
                    {{ formErrorMessage }}
                </div>
                <hr class="ds-divider ds-modal-dialog__bottom-divider" />
                <div class="contest-form__button">
                    <DsButton html-type="submit">Odeslat</DsButton>
                </div>
            </form>
        </template>
    </DsModalDialog>
</template>

<script lang="ts" setup>
import { getCurrentInstance, nextTick, onMounted, ref } from "vue";
import { vMaska } from "maska/vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsIcon from "@sazka/design-system/src/icon/ds-icon.vue";
import DsModalDialog from "@sazka/design-system/src/modal-dialog/ds-modal-dialog.vue";
import DsTopSlotHeadlineSubheadline from "@sazka/design-system/src/top-slot/ds-top-subheadline.vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";
import { triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { ContestFormData, ContestFormConfiguration } from "../models/contest-form-models";

const props = defineProps<{
    configuration: ContestFormConfiguration;
    identifier: number;
}>();

const app = getCurrentInstance();

const formErrorMessage = ref<string>("");
const formData = ref<ContestFormData>({} as ContestFormData);
const isModalVisible = ref(false);

const handleRulesClick = (e: Event) => {
    const clickLink = e.target?.["href"];
    if (clickLink && environment.isInAppView) {
        e.preventDefault();
        triggerMobileAppEvent("openPdf", clickLink);
    }
};

const closeModal = () => {
    nextTick(() => {
        app?.appContext.app.unmount();
    });
};

const validateForm = () => {
    if (
        !formData.value.firstName ||
        !formData.value.lastName ||
        (props.configuration.isPhoneRequired && !formData.value.phone) ||
        (props.configuration.isEmailRequired && !formData.value.email) ||
        !formData.value.hasAgreed ||
        !formData.value.text
    ) {
        formErrorMessage.value = "Jejda, odeslání se nepodařilo. Musíte vyplnit všechna povinná pole.";
        return false;
    } else {
        formErrorMessage.value = "";
        return true;
    }
};

const submitForm = async () => {
    if (!validateForm()) {
        return;
    }

    Loader.show();

    try {
        await httpClient.post("/api/landing-page/contest-form", formData.value, {
            params: {
                identifier: props.identifier,
            },
        });
        formData.value = {} as ContestFormData;
        closeModal();
        showAlert({
            title: "Byli jste zařazeni do soutěže",
            subText: "Děkujeme za vaši registraci! Výherce budeme brzy kontaktovat na zadané kontaktní údaje.",
            btnCloseText: "Rozumím",
            alertImageSrc: "/img/registration/kulicnik-check.png",
        });
    } catch {
        showAlert({
            title: "Odeslání se nezdařilo, zkuste to prosím později.",
            btnCloseText: "Rozumím",
        });
    } finally {
        Loader.hide();
    }
};

onMounted(() => {
    isModalVisible.value = true;
});
</script>
